import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { DatePicker } from '@plato/ui-legacy/components/ui/date-picker';
import { InputNumber } from '@plato/ui-legacy/components/ui/input-number';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { formatWeekdayShort } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { cn } from '@plato/ui/util/classes';
import { formatISO, parseISO, setDay } from 'date-fns';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecurrenceRule } from '../../../utils/rrule.ts';
import { defaultRecurrenceRule } from '../utils/recurrence-rule.ts';

type RRuleInputProps = {
    startDate: string;
    value?: RecurrenceRule;
    onChange: (value: RecurrenceRule) => void;
    disabled?: boolean;
};

const MAX_INTERVAL = 99;

function toggleWeekday(rule: RecurrenceRule, weekday: number): RecurrenceRule {
    const weekdays = rule.weekdays.includes(weekday)
        ? rule.weekdays.filter((day) => day !== weekday)
        : [...rule.weekdays, weekday];

    return { ...rule, weekdays: weekdays.toSorted((a, b) => a - b) };
}
/** ISO 8601 order, Monday first, which is also how the buttons read in German and English. */
const WEEKDAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

export const RRuleInput = forwardRef<HTMLDivElement, RRuleInputProps>(function RRuleInput(
    { value, onChange, startDate, disabled = false },
    ref,
) {
    const { t } = useTranslation('events');
    const locale = useResolvedLocale();
    const { defaultCurrency } = useAppConfig();

    // GROW-99: the rule is plain data now. It used to be serialised to an RRULE string on every
    // keystroke and parsed straight back to recover the same numbers.
    const rule = value ?? defaultRecurrenceRule(startDate);
    const { weeks, weekdays, until } = rule;

    return (
        <div ref={ref} className="flex flex-col gap-3">
            <div className="flex gap-3 flex-col sm:flex-row">
                <div className="flex flex-col gap-1.5">
                    <div>
                        <Text color="neutral-950">{t('events__form_recurrence_repeat_every')}</Text>
                    </div>
                    <div className="flex items-center gap-3">
                        <InputNumber
                            currency={defaultCurrency}
                            aria-label={t('events__form_recurrence_repeat_every')}
                            data-testid="recurrence-interval"
                            className="w-20 text-left hover:border-neutral-400 focus-visible:border-blue-600 focus-visible:ring-1 focus-visible:ring-blue-200 focus-visible:ring-offset-0"
                            value={weeks}
                            decimalScale={0}
                            allowNegative={false}
                            isAllowed={({ floatValue }) =>
                                floatValue === undefined || (floatValue >= 1 && floatValue <= MAX_INTERVAL)
                            }
                            onValueChange={(values) => {
                                if (values.floatValue !== undefined) {
                                    onChange({ ...rule, weeks: values.floatValue });
                                }
                            }}
                            disabled={disabled}
                        />
                        <Text color="neutral-950">{t('events__form_recurrence_weeks')}</Text>
                    </div>
                </div>
                <div className="flex flex-col gap-1.5">
                    <div>
                        <Text color="neutral-950">{t('events__form_recurrence_on')}</Text>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {WEEKDAY_OPTIONS.map((weekday) => {
                            const isSelected = weekdays.includes(weekday);
                            // The last selected weekday cannot be cleared: a rule with no weekday
                            // produces no occurrences at all, and the schema rejects it.
                            const isLastSelected = isSelected && weekdays.length === 1;

                            return (
                                <Button
                                    key={weekday}
                                    variant="outline"
                                    size="icon-s"
                                    className={cn('text-sm', {
                                        'bg-blue-50': isSelected,
                                    })}
                                    rounded={true}
                                    selected={isSelected}
                                    onClick={() => {
                                        if (isLastSelected) {
                                            return;
                                        }
                                        onChange(toggleWeekday(rule, weekday));
                                    }}
                                    disabled={disabled}
                                >
                                    {/* setDay wants a JS weekday (0=Sun), the one place the two
                                        encodings still meet, and only to render a label. */}
                                    {formatWeekdayShort(setDay(new Date(), weekday % 7), locale).charAt(0)}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-1.5">
                <div>
                    <Text color="neutral-950">{t('events__form_recurrence_until_label')}</Text>
                </div>
                <div className="flex items-center">
                    <DatePicker
                        title={t('events__form_recurrence_until_label')}
                        value={until ? parseISO(until) : undefined}
                        onChange={(date) => {
                            onChange({
                                ...rule,
                                until: date ? formatISO(date, { representation: 'date' }) : undefined,
                            });
                        }}
                        disabled={disabled}
                        clearable
                    />
                </div>
            </div>
        </div>
    );
});
